import { FC, useEffect, useId } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { cn } from '../../../../../utilities/cn';
import TextBlockLayoutControls from '../../../raidr/kidsV2/components/inputs/TextBlockLayoutControls';
import TextBlockStyleControls from '../../../raidr/kidsV2/components/inputs/TextBlockStyleControls';
import TextInputTipTap from '../../../raidr/kidsV2/components/inputs/TextInputTipTap';
import type {
  FormatOptions,
  PdfElement,
} from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';

type TemplateEditorFormValues = {
  content: string;
  format_options: FormatOptions;
};

const TEMPLATE_FIELD_PATHS = {
  content: 'content',
  formatOptions: 'format_options',
} as const;

const TemplateContentEditor: FC<{
  value: string;
  onChange: (value: string) => void;
  formatOptions?: FormatOptions;
  onFormatOptionsChange?: (value: FormatOptions) => void;
  disabled?: boolean;
  minHeightClass?: string;
  /** Grow to fill parent; scroll inside the editable area */
  fillHeight?: boolean;
  className?: string;
}> = ({
  value,
  onChange,
  formatOptions: formatOptionsProp,
  onFormatOptionsChange,
  disabled = false,
  minHeightClass = 'min-h-[20rem]',
  fillHeight = false,
  className = '',
}) => {
    const editorId = useId();

    const methods = useForm<TemplateEditorFormValues>({
      defaultValues: {
        content: value,
        format_options: formatOptionsProp ?? {},
      },
    });

    useEffect(() => {
      const currentContent = methods.getValues('content');
      if (currentContent !== value) {
        methods.setValue('content', value, { shouldDirty: false });
      }
    }, [methods, value]);

    useEffect(() => {
      const currentFormatOptions = methods.getValues('format_options');
      const nextFormatOptions = formatOptionsProp ?? {};
      if (
        JSON.stringify(currentFormatOptions) !== JSON.stringify(nextFormatOptions)
      ) {
        methods.setValue('format_options', nextFormatOptions, {
          shouldDirty: false,
        });
      }
    }, [formatOptionsProp, methods]);

    const watchedContent = useWatch({
      control: methods.control,
      name: 'content',
    });
    const watchedFormatOptions = useWatch({
      control: methods.control,
      name: 'format_options',
    });

    useEffect(() => {
      if (watchedContent !== undefined && watchedContent !== value) {
        onChange(watchedContent);
      }
    }, [onChange, value, watchedContent]);

    useEffect(() => {
      if (!onFormatOptionsChange || watchedFormatOptions === undefined) return;
      const currentProp = formatOptionsProp ?? {};
      if (JSON.stringify(watchedFormatOptions) !== JSON.stringify(currentProp)) {
        onFormatOptionsChange(watchedFormatOptions);
      }
    }, [
      formatOptionsProp,
      onFormatOptionsChange,
      watchedFormatOptions,
    ]);

    const editorField = {
      content: watchedContent ?? value,
    } as PdfElement;

    return (
      <FormProvider {...methods}>
        <div
          className={cn(
            'relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm',
            fillHeight && 'flex h-full min-h-0 flex-col',
            disabled && 'opacity-60',
            className,
          )}
        >
          <div
            className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-200 bg-gray-50/80 px-3 py-2"
          >
            <TextBlockLayoutControls
              formatOptionsPath={TEMPLATE_FIELD_PATHS.formatOptions}
              disabled={disabled}
              compact
            />
            <div className="h-5 w-px shrink-0 bg-gray-200" aria-hidden />
            <TextBlockStyleControls
              formatOptionsPath={TEMPLATE_FIELD_PATHS.formatOptions}
              idPrefix={editorId}
              disabled={disabled}
              compact
            />
          </div>

          <div
            className={cn(
              fillHeight && 'min-h-[8rem] flex-1 overflow-y-auto',
            )}
          >
            <TextInputTipTap
              field={editorField}
              index={0}
              fieldPaths={TEMPLATE_FIELD_PATHS}
              isReadOnly={disabled}
              embedded
              customClassName={cn(
                'outline-none px-3 py-2.5 text-sm text-gray-900',
                fillHeight ? 'min-h-full' : minHeightClass,
              )}
            />
          </div>
        </div>
      </FormProvider>
    );
  };

export default TemplateContentEditor;
